/*
 * Sleep sounds, synthesized with Web Audio: no audio files, so they play offline. Every sound is a looping
 * buffer: rain comes from rainSynth.ts, the others are noise shaped by filters, and the ocean adds a slow swell
 * with an LFO.
 */
import { RAIN_SAMPLE_RATE, rainSamples } from './rainSynth';

export type SleepSound = 'rain' | 'brown' | 'ocean';

export const SLEEP_SOUNDS: SleepSound[] = ['rain', 'brown', 'ocean'];
export const FADE_SECONDS = 20;

const BUFFER_SECONDS = 4;
/** Long enough that the ear does not catch individual drops coming round again. */
const RAIN_BUFFER_SECONDS = 45;

/** White noise, or brown noise (integrated white noise, 6 dB/octave) when `brown` is set. */
export const noiseSamples = (
    length: number,
    brown: boolean,
    random: () => number = Math.random
): Float32Array => {
    const out = new Float32Array(length);
    let last = 0;
    for (let i = 0; i < length; i++) {
        const white = random() * 2 - 1;
        if (brown) {
            last = (last + 0.02 * white) / 1.02;
            out[i] = last * 3.5;
        } else {
            out[i] = white;
        }
    }
    return out;
};

export interface SleepPlayer {
    start: (sound: SleepSound) => void;
    /** Fades out over `seconds`. Call `stop` afterwards. */
    fadeOut: (seconds: number) => void;
    stop: () => void;
    analyser: () => AnalyserNode | null;
    close: () => void;
}

const contextClass = (): typeof AudioContext | undefined =>
    typeof window === 'undefined'
        ? undefined
        : window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext;

/** `create` is injectable for tests; by default the browser's AudioContext. */
export const createSleepPlayer = (
    create: () => AudioContext | null = () => {
        const Ctor = contextClass();
        return Ctor ? new Ctor() : null;
    }
): SleepPlayer => {
    let ctx: AudioContext | null = null;
    let master: GainNode | null = null;
    let analyser: AnalyserNode | null = null;
    let sources: AudioScheduledSourceNode[] = [];

    const ensure = () => {
        if (!ctx) {
            ctx = create();
            if (!ctx) return null;
            master = ctx.createGain();
            analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.72;
            analyser.minDecibels = -85;
            analyser.maxDecibels = -25;
            master.connect(analyser);
            analyser.connect(ctx.destination);
        }
        return ctx;
    };

    const loop = (
        c: AudioContext,
        samples: Float32Array,
        sampleRate: number
    ) => {
        const buffer = c.createBuffer(1, samples.length, sampleRate);
        buffer.getChannelData(0).set(samples);
        const source = c.createBufferSource();
        source.buffer = buffer;
        source.loop = true;
        sources.push(source);
        return source;
    };

    const noise = (c: AudioContext) =>
        loop(
            c,
            noiseSamples(Math.floor(c.sampleRate * BUFFER_SECONDS), true),
            c.sampleRate
        );

    let rain: Float32Array | null = null;

    const stop = () => {
        sources.forEach((s) => {
            try {
                s.stop();
            } catch {
                // Already stopped.
            }
            s.disconnect();
        });
        sources = [];
    };

    const start = (sound: SleepSound) => {
        const c = ensure();
        if (!c || !master) return;
        stop();
        void c.resume?.();
        master.gain.cancelScheduledValues(c.currentTime);
        master.gain.value = 0.7;
        const level = c.createGain();
        level.connect(master);
        if (sound === 'rain') {
            // The recipe has its own filters and is written at a fixed rate; Web Audio resamples the buffer.
            rain = rain ?? rainSamples(RAIN_SAMPLE_RATE * RAIN_BUFFER_SECONDS);
            const source = loop(c, rain, RAIN_SAMPLE_RATE);
            level.gain.value = 1;
            source.connect(level);
            source.start();
            return;
        }
        const filter = c.createBiquadFilter();
        const source = noise(c);
        filter.type = 'lowpass';
        filter.frequency.value = sound === 'brown' ? 900 : 500;
        level.gain.value = sound === 'brown' ? 0.6 : 0.5;
        source.connect(filter);
        filter.connect(level);
        source.start();
        if (sound === 'ocean') {
            // A wave every ~9 s: the LFO moves the cutoff and the loudness together.
            const lfo = c.createOscillator();
            lfo.frequency.value = 0.11;
            const depthGain = c.createGain();
            depthGain.gain.value = 0.35;
            const depthFreq = c.createGain();
            depthFreq.gain.value = 350;
            lfo.connect(depthGain);
            lfo.connect(depthFreq);
            depthGain.connect(level.gain);
            depthFreq.connect(filter.frequency);
            sources.push(lfo);
            lfo.start();
        }
    };

    const fadeOut = (seconds: number) => {
        if (!ctx || !master) return;
        const now = ctx.currentTime;
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(0, now + seconds);
    };

    return {
        start,
        fadeOut,
        stop,
        analyser: () => analyser,
        close: () => {
            stop();
            void ctx?.close?.();
            ctx = null;
            master = null;
            analyser = null;
        },
    };
};
